import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Pencil, Plus, Search, ShoppingCart, Trash2 } from "lucide-react";
import { api, apiError, downloadCsv, formatDate, money, moneyExact, todayISO } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import StickyFilters from "@/components/StickyFilters";
import DateRange, { ALL_TIME, rangeNote, withinRange } from "@/components/DateRange";
import { useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import ItemPicker from "@/components/ItemPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { EmptyState, ItemCode, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Purchases() {
  const confirm = useConfirm();
  const { settings, categories } = useSettings();
  const sym = settings.currency_symbol;
  const { tenantId } = useTenant();
  const CK = cacheKey("purchases", tenantId);
  const cached = cacheGet(CK);
  const [rows, setRows] = useState(() => cached?.rows || []);
  const [items, setItems] = useState(() => cached?.items || []);
  const [suppliers, setSuppliers] = useState(() => cached?.suppliers || []);
  const [loading, setLoading] = useState(() => !cached);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState(ALL_TIME);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    item_code: "",
    quantity: "",
    unit_cost: "",
    supplier: "",
    purchase_date: todayISO(),
  });
  const [busy, setBusy] = useState(false);

  // Ignore a reply that lands after the client has been switched — otherwise
  // one business's figures can appear under another's name.
  const currentKey = useRef(CK);
  useEffect(() => {
    currentKey.current = CK;
  }, [CK]);

  const load = useCallback(async () => {
    const askedFor = CK;
    if (!cacheGet(CK)) setLoading(true);
    try {
      const [p, i, s] = await Promise.all([
        api.get("/purchases"),
        api.get("/items"),
        api.get("/suppliers"),
      ]);
      if (currentKey.current !== askedFor) return;
      setRows(p.data);
      setItems(i.data);
      setSuppliers(s.data);
      cacheSet(CK, { rows: p.data, items: i.data, suppliers: s.data });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const inRange = (row) => withinRange(row.purchase_date, range);
    const q = query.trim().toLowerCase();
    const base = rows.filter(inRange);
    if (!q) return base;
    return base.filter(
      (r) =>
        r.item_code.toLowerCase().includes(q) ||
        (r.item_name || "").toLowerCase().includes(q) ||
        (r.supplier || "").toLowerCase().includes(q)
    );
  }, [rows, query, range]);

  const totals = useMemo(
    () => ({
      spend: visible.reduce((t, r) => t + (r.total_cost || 0), 0),
      units: visible.reduce((t, r) => t + (r.quantity || 0), 0),
    }),
    [visible]
  );

  // Editing reuses the same form, filled in. Only an existing item can be
  // chosen — creating one from an edit would be a surprise.
  const openEditor = (row) => {
    setEditingId(row.purchase_id);
    setForm({
      isNew: false,
      item_code: row.item_code,
      new_name: "",
      new_category: "",
      new_price: "",
      quantity: String(row.quantity),
      unit_cost: String(row.unit_cost),
      supplier: row.supplier || "",
      purchase_date: row.purchase_date,
    });
    setAdding(true);
  };

  const openDialog = () => {
    setEditingId(null);
    setForm({
      isNew: false,
      item_code: "",
      new_name: "",
      new_category: "",
      new_price: "",
      quantity: "",
      unit_cost: "",
      supplier: "",
      purchase_date: todayISO(),
    });
    setAdding(true);
  };

  const submit = async () => {
    if (form.isNew) {
      if (!form.new_name.trim() || !form.new_category) {
        toast.error("Give it a name and a category");
        return;
      }
    } else if (!form.item_code) {
      toast.error("Choose an item");
      return;
    }
    if (!Number(form.quantity)) {
      toast.error("Enter a quantity");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        purchase_date: form.purchase_date,
        supplier: form.supplier,
        quantity: Number(form.quantity),
        unit_cost: Number(form.unit_cost) || 0,
        ...(form.isNew
          ? {
              new_item: {
                name: form.new_name,
                category: form.new_category,
                selling_price: form.new_price ? Number(form.new_price) : null,
              },
            }
          : { item_code: form.item_code }),
      };
      const { data } = editingId
        ? await api.put(`/purchases/${editingId}`, payload)
        : await api.post("/purchases", payload);
      toast.success(
        editingId
          ? "Purchase updated — stock adjusted"
          : data.created_item
          ? `${data.created_item.item_code} added and ${form.quantity} in stock`
          : "Purchase logged — stock updated"
      );
      setAdding(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Remove this purchase of ${row.item_name}?`,
      body: `${row.quantity} unit${row.quantity === 1 ? "" : "s"} will come back out of stock.`,
      confirmText: "Remove purchase",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/purchases/${row.purchase_id}`);
      toast.success("Purchase removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Purchases"
      subtitle={
        // Held back until the data arrives — otherwise it briefly
        // describes an empty page.
        loading
          ? undefined
          : `${visible.length} entries · ${totals.units} units · ${money(
              totals.spend,
              sym
            )} spent${rangeNote(range)}`
      }
      action={
        <>
          <Button
            variant="outline"
            onClick={() => downloadCsv("/export/purchases.csv", "purchase-log.csv")}
            className="hidden sm:inline-flex"
          >
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={openDialog} className="bg-brand hover:bg-brand-hover">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Log purchase</span>
          </Button>
        </>
      }
    >
      <StickyFilters>
        <div className="flex flex-col sm:flex-row gap-2.5">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by item or supplier"
              className="pl-9 border-line"
            />
          </div>
          <DateRange value={range} onChange={setRange} />
        </div>
      </StickyFilters>

      {loading ? (
        <LoadingPanel rows={7} />
      ) : visible.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={ShoppingCart}
            title={query ? "No purchases match that search" : "No purchases logged"}
            body={
              query
                ? "Try a different supplier or item name."
                : "Every purchase you log adds to the stock count for that item."
            }
            action={
              !query && (
                <Button onClick={openDialog} className="bg-brand hover:bg-brand-hover">
                  <Plus className="w-4 h-4 mr-2" /> Log purchase
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-line bg-surface/60">
                  {["Date", "Code", "Item", "Supplier", "Qty", "Unit cost", "Total", ""].map(
                    (h, idx) => (
                      <th
                        key={h + idx}
                        className={`t-head px-4 py-3 text-left ${
                          ["Qty", "Unit cost", "Total"].includes(h) ? "text-right" : ""
                        }`}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.purchase_id} className="t-row last:border-0 group">
                    <td className="t-cell text-muted2 whitespace-nowrap">
                      {formatDate(r.purchase_date)}
                    </td>
                    <td className="t-cell">
                      <ItemCode code={r.item_code} />
                    </td>
                    <td className="t-cell font-medium max-w-[240px] truncate">{r.item_name}</td>
                    <td className="t-cell text-muted2 max-w-[200px] truncate">
                      {r.supplier || "—"}
                    </td>
                    <td className="t-cell num font-mono">{r.quantity}</td>
                    <td className="t-cell num text-muted2">{money(r.unit_cost, sym)}</td>
                    <td className="t-cell num font-medium">{moneyExact(r.total_cost, sym)}</td>
                    <td className="t-cell text-right">
                      <button
                        onClick={() => openEditor(r)}
                        className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                        title="Edit purchase"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(r)}
                        className="text-muted2 hover:text-stock-out sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1"
                        title="Remove purchase"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingId ? "Edit this purchase" : "Log a purchase"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "The item's stock is corrected to match whatever you change."
                : "This adds to the item's stock straight away."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* A purchase is often the first time an item exists at all, so it
                can be described here rather than added separately first. */}
            <div className={cn("flex rounded-lg border border-line p-0.5", editingId && "hidden")}>
              {[
                { key: false, label: "Something I stock" },
                { key: true, label: "Something new" },
              ].map((opt) => (
                <button
                  key={String(opt.key)}
                  type="button"
                  onClick={() => setForm({ ...form, isNew: opt.key, item_code: "" })}
                  className={cn(
                    "flex-1 py-1.5 rounded-md text-[12.5px] font-medium transition-colors",
                    form.isNew === opt.key
                      ? "bg-brand-light text-brand"
                      : "text-muted2 hover:text-ink"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {form.isNew ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ni">What is it?</Label>
                  <Input
                    id="ni"
                    value={form.new_name}
                    onChange={(e) => setForm({ ...form, new_name: e.target.value })}
                    placeholder="What you call it"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Category</Label>
                    <Select
                      value={form.new_category}
                      onValueChange={(v) => setForm({ ...form, new_category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.category_id} value={c.name}>
                            {c.prefix} · {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="np">Sells for</Label>
                    <Input
                      id="np"
                      type="number"
                      min="0"
                      value={form.new_price}
                      onChange={(e) => setForm({ ...form, new_price: e.target.value })}
                      placeholder={
                        form.unit_cost
                          ? String(
                              Math.round(
                                Number(form.unit_cost) *
                                  Number(settings.markup_multiplier || 2.5)
                              )
                            )
                          : "Optional"
                      }
                    />
                    <p className="text-[11.5px] text-muted2">
                      Left blank, it's {settings.markup_multiplier || 2.5}× the cost.
                    </p>
                  </div>
                </div>
                <p className="text-[12px] text-muted2 leading-relaxed">
                  It'll be added to Inventory with a code from that category, and this
                  purchase puts the first stock on the shelf.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Item</Label>
                <ItemPicker
                  items={items}
                  value={form.item_code}
                  onChange={(code, item) =>
                    setForm({
                      ...form,
                      item_code: code,
                      unit_cost: form.unit_cost || item.unit_cost || "",
                    })
                  }
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pq">Quantity</Label>
                <Input
                  id="pq"
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pc">Unit cost</Label>
                <Input
                  id="pc"
                  type="number"
                  min="0"
                  value={form.unit_cost}
                  onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ps">Supplier</Label>
              <Input
                id="ps"
                list="supplier-list"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                placeholder="Where you bought it"
              />
              <datalist id="supplier-list">
                {suppliers.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pd">Purchase date</Label>
              <Input
                id="pd"
                type="date"
                value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
              />
            </div>

            {form.quantity && form.unit_cost && (
              <div className="rounded-lg bg-brand-light px-3.5 py-2.5 text-[13px] text-brand flex items-center justify-between">
                <span>Total cost</span>
                <span className="font-mono font-semibold">
                  {moneyExact(Number(form.quantity) * Number(form.unit_cost), sym)}
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
              {editingId ? "Save changes" : "Log purchase"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
